// Restrictive Negatives — judgment row, Negatives section.
// LLM-drafted verdict on whether the negative-keyword terms in effect are over-restrictive
// (the verdict block is rendered above the collapse boundary by FindingCard). This renderer
// supplies the "negatives Claude saw" supporting table for verification, sorted by how many
// places each term appears.

import type { EvidenceProps } from './registry';

type NegativeMatchType = 'EXACT' | 'PHRASE' | 'BROAD' | 'UNSPECIFIED' | 'UNKNOWN';

interface TermRow {
  text: string;
  matchType: NegativeMatchType;
  count: number;
  scopes: string[];
  locations: string[];
}

interface RestrictiveNegativesShape {
  totalNegatives: number;
  uniqueTerms: number;
  byScope: { shared: number; campaign: number; adGroup: number };
  attachedSharedLists: number;
  truncatedForReview: number;
  terms: TermRow[];
  // Whether ad-group-level negatives were reviewed. Absent → assumed true (back-compat with
  // existing Google findings). Microsoft sets false (ad-group negatives aren't pulled).
  adGroupAssessed?: boolean;
}

const MATCH_TYPE_LABEL: Record<NegativeMatchType, string> = {
  EXACT: 'Exact',
  PHRASE: 'Phrase',
  BROAD: 'Broad',
  UNSPECIFIED: '—',
  UNKNOWN: '—'
};

const SCOPE_LABEL: Record<string, string> = {
  shared: 'Shared list',
  campaign: 'Campaign',
  adGroup: 'Ad group'
};

export const RestrictiveNegativesEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as RestrictiveNegativesShape;
  const adGroupAssessed = e.adGroupAssessed !== false;

  if (e.totalNegatives === 0) {
    return (
      <div className='evidence'>
        <p className='evidence__footnote'>
          No negative keywords are in effect on enabled Search campaigns — no terms to review.
        </p>
      </div>
    );
  }

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Negatives in effect
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.uniqueTerms.toLocaleString()} unique terms
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.totalNegatives.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Total negatives</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.byScope.shared.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>In shared lists</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.byScope.campaign.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Campaign-level</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>
              {adGroupAssessed ? e.byScope.adGroup.toLocaleString() : 'n/a'}
            </div>
            <div className='audience-coverage__lbl'>
              Ad-group-level{adGroupAssessed ? '' : ' (not reviewed)'}
            </div>
          </div>
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Negatives Claude saw
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.terms.length.toLocaleString()} shown
          </span>
        </h4>
        <table className='evidence__table evidence__table--restrictive-negatives'>
          <thead>
            <tr>
              <th>Negative term</th>
              <th>Match type</th>
              <th>Where</th>
              <th className='evidence__th--right'>Occurrences</th>
            </tr>
          </thead>
          <tbody>
            {e.terms.map(t => (
              <tr key={`${t.text}␟${t.matchType}`}>
                <td>{t.text}</td>
                <td>{MATCH_TYPE_LABEL[t.matchType] ?? t.matchType}</td>
                <td>
                  <span className='evidence__modifier-list'>
                    {t.scopes.map(s => (
                      <span key={s} className='evidence__audience-chip'>
                        {SCOPE_LABEL[s] ?? s}
                      </span>
                    ))}
                  </span>
                </td>
                <td className='evidence__td--right'>{t.count.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {e.truncatedForReview > 0 && (
          <p className='evidence__inline-note'>
            {e.truncatedForReview.toLocaleString()} additional lower-frequency negative term
            {e.truncatedForReview === 1 ? '' : 's'} not shown (and not sent to Claude). The most
            pervasive negatives are reviewed first.
          </p>
        )}
      </section>

      <p className='evidence__footnote'>
        Only negatives actually in effect on enabled Search campaigns are reviewed — shared-list
        members are scoped to the {e.attachedSharedLists.toLocaleString()} list
        {e.attachedSharedLists === 1 ? '' : 's'} attached to at least one enabled Search campaign.
        With Broad Match + Smart Bidding, negatives should be used sparingly (primarily brand
        safety); the verdict flags terms that may block traffic the account would want.
      </p>
    </div>
  );
};
